import { useMutation } from '@tanstack/react-query';
import bookingApi from '@/features/public/booking/api/bookingApi.ts';
import { BookingResponseDto } from '@/features/public/booking/types/BookingResponseDto.ts';
import { ClientFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';
import { toBookingRequest } from '@/features/public/booking/types/BookingSpecification.mapper.ts';
import { toast } from 'sonner';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import {
  isBookingPriceChangedError,
  isRegistrationUnavailableError,
} from '@/features/public/booking/api/bookingErrors.ts';
import { useRouter } from '@tanstack/react-router';
import { queryClient } from '@/App.tsx';
import { bookingApiQueryKeys } from '@/features/public/booking/api/bookingApi.queryKeys.ts';

interface CreateBookingParams {
  orgSlug: string;
  tourSlug: string;
  clientForm: ClientFormWithCustomFields;
  fieldDefinitions: CustomFieldDefinitionDto[];
  acceptedConsentIds: string[];
  promoCodes: string[];
  // Echo total z ostatniej wyceny (quote) widocznej dla użytkownika — nie liczyć po stronie frontendu
  expectedTotals: MoneyValueDto[];
}

export const useCreateBooking = () => {
  const router = useRouter();

  return useMutation<BookingResponseDto, Error, CreateBookingParams>({
    mutationFn: async ({
      orgSlug,
      tourSlug,
      clientForm,
      fieldDefinitions,
      acceptedConsentIds,
      promoCodes,
      expectedTotals,
    }) => {
      const bookingRequest = toBookingRequest(
        clientForm,
        fieldDefinitions,
        promoCodes,
      );
      bookingRequest.acceptedConsentIds = acceptedConsentIds;
      bookingRequest.expectedTotals = expectedTotals;
      return bookingApi.createBooking(orgSlug, tourSlug, bookingRequest);
    },
    onError: async (error: Error, { orgSlug, tourSlug }) => {
      // Zmianę ceny (409) obsługuje strona podsumowania: odświeża wycenę i prosi o ponowne potwierdzenie
      if (isBookingPriceChangedError(error)) {
        return;
      }
      // Okno zapisów zamknęło się (albo miejsca się skończyły) w trakcie
      // wypełniania formularza — dane, na których zbudowaliśmy widok, są
      // nieaktualne. Świeże `basicInfo` przełączy route na właściwy ekran.
      if (isRegistrationUnavailableError(error)) {
        await queryClient.invalidateQueries({
          queryKey: bookingApiQueryKeys.basicInfo(orgSlug, tourSlug),
        });
        await router.invalidate();
      }
      toast.error('Coś poszło nie tak', {
        description: error.message,
      });
    },
  });
};
